import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Image as ImageIcon, Loader2, Film } from 'lucide-react';
import { listCaptures, resolveCaptureBySlug, resolveTenant, updatePromotedContent } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

interface PromotedItem {
  type: 'capture';
  id: number;
  title: string;
  coverUrl: string | null;
  captureSlug?: string;
}

interface CaptureItem {
  id: number;
  title: string;
  imageUrl: string | null;
  slug: string;
  trackId: number | null;
  mediaType?: 'track' | 'video';
}

interface PromotedCaptureRenderData {
  mediaType: 'track' | 'video';
  title: string;
  description: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  backgroundMode: 'automatic' | 'solid' | 'image';
  backgroundColor: string;
  backgroundImageUrl: string | null;
  backgroundOverlayOpacity: number;
  audioStart: number;
  playbackLength: number | null;
  playbackType: 'preview' | 'full';
  streamUrl: string | null;
  songLength: number | null;
}

interface ArtistPromotedContentProps {
  slug: string;
}

export default function ArtistPromotedContent({ slug }: ArtistPromotedContentProps) {
  const { token } = useAuthStore();
  const [promotedItem, setPromotedItem] = useState<PromotedItem | null>(null);
  const [availableCaptures, setAvailableCaptures] = useState<CaptureItem[]>([]);
  const [renderData, setRenderData] = useState<PromotedCaptureRenderData | null>(null);
  const [renderDataLoading, setRenderDataLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const [tenantRes, capturesRes] = await Promise.all([
          resolveTenant(slug),
          listCaptures(slug),
        ]);

        let captureRows: CaptureItem[] = [];
        if (capturesRes.ok) {
          const capturesJson = await capturesRes.json() as { data: CaptureItem[] };
          captureRows = capturesJson.data || [];
          setAvailableCaptures(captureRows);
        }

        if (tenantRes.ok) {
          const tenantJson = await tenantRes.json() as {
            data: {
              promotedContent?: {
                type: 'vault' | 'capture';
                id: number;
                title: string;
                coverUrl: string | null;
              } | null;
            };
          };

          if (tenantJson.data?.promotedContent?.type === 'capture') {
            const matchingCapture = captureRows.find((capture) => capture.id === tenantJson.data.promotedContent?.id);
            setPromotedItem({
              type: 'capture',
              id: tenantJson.data.promotedContent.id,
              title: tenantJson.data.promotedContent.title,
              coverUrl: tenantJson.data.promotedContent.coverUrl,
              captureSlug: matchingCapture?.slug,
            });
          }
        }
      } catch {
        // ignored intentionally
      }
    }

    loadInitialData();
  }, [slug]);

  const activeCaptures = useMemo(() => {
    return availableCaptures.filter((capture) => {
      const mediaType = capture.mediaType === 'video' ? 'video' : 'track';
      return mediaType === 'video' || !!capture.trackId;
    });
  }, [availableCaptures]);

  const resolvedCaptureSlug = useMemo(() => {
    if (!promotedItem) return null;
    if (promotedItem.captureSlug) return promotedItem.captureSlug;
    const match = availableCaptures.find((capture) => capture.id === promotedItem.id);
    return match?.slug || null;
  }, [promotedItem, availableCaptures]);

  useEffect(() => {
    async function loadRenderData() {
      if (!resolvedCaptureSlug) {
        setRenderData(null);
        return;
      }

      setRenderDataLoading(true);
      try {
        const res = await resolveCaptureBySlug(slug, resolvedCaptureSlug);
        if (!res.ok) {
          setRenderData(null);
          return;
        }

        const json = await res.json() as {
          data: {
            title: string;
            description?: string | null;
            mediaType?: 'track' | 'video';
            imageUrl?: string | null;
            videoThumbnailUrl?: string | null;
            videoUrl?: string | null;
            backgroundMode?: 'automatic' | 'solid' | 'image';
            backgroundColor?: string | null;
            backgroundImageUrl?: string | null;
            backgroundOverlayOpacity?: number;
            audioStart?: number | null;
            playbackLength?: number | null;
            playbackType?: 'preview' | 'full';
            track?: {
              streamUrl?: string | null;
              songLength?: number | null;
            } | null;
          };
        };

        setRenderData({
          mediaType: json.data.mediaType === 'video' ? 'video' : 'track',
          title: json.data.title,
          description: json.data.description || null,
          imageUrl: json.data.imageUrl || null,
          videoUrl: json.data.videoUrl || null,
          backgroundMode: json.data.backgroundMode === 'solid' || json.data.backgroundMode === 'image' ? json.data.backgroundMode : 'automatic',
          backgroundColor: json.data.backgroundColor || '#0D0D0D',
          backgroundImageUrl: json.data.backgroundImageUrl || null,
          backgroundOverlayOpacity: Math.max(0, Math.min(90, Number(json.data.backgroundOverlayOpacity ?? 35))),
          audioStart: Number(json.data.audioStart ?? 0),
          playbackLength: typeof json.data.playbackLength === 'number' ? json.data.playbackLength : null,
          playbackType: json.data.playbackType === 'full' ? 'full' : 'preview',
          streamUrl: json.data.track?.streamUrl || null,
          songLength: typeof json.data.track?.songLength === 'number' ? json.data.track.songLength : null,
        });
      } catch {
        setRenderData(null);
      } finally {
        setRenderDataLoading(false);
      }
    }

    loadRenderData();
  }, [slug, resolvedCaptureSlug]);

  const handleSelectItem = useCallback(async (item: PromotedItem) => {
    setPromotedItem(item);
    setShowPicker(false);
    setSaving(true);
    try {
      await updatePromotedContent(slug, {
        promotedContentType: 'capture',
        promotedContentId: item.id,
      }, token);
    } catch (err) {
      console.warn('[PromotedContent] Failed to save:', err);
    } finally {
      setSaving(false);
    }
  }, [slug, token]);

  const handleClear = useCallback(async () => {
    setPromotedItem(null);
    setRenderData(null);
    setSaving(true);
    try {
      await updatePromotedContent(slug, {
        promotedContentType: null,
        promotedContentId: null,
      }, token);
    } catch (err) {
      console.warn('[PromotedContent] Failed to clear:', err);
    } finally {
      setSaving(false);
    }
  }, [slug, token]);

  const backgroundOverlayAlpha = (renderData?.backgroundOverlayOpacity ?? 35) / 100;

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-white">Promoted Content</h2>
        {saving && <Loader2 className="w-4 h-4 text-electric-cyan animate-spin" />}
      </div>

      <div className="relative rounded-xl overflow-hidden bg-matte-black border border-white/5">
        <div className="relative p-4">
          {promotedItem ? (
            <div
              className="relative cursor-pointer"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => setShowPicker(true)}
            >
              {(renderData?.mediaType !== 'track') && (
                <div className="mb-3 pr-24">
                  <h3 className="text-sm sm:text-base font-semibold text-white leading-tight line-clamp-2">
                    {renderData?.title || promotedItem.title}
                  </h3>
                  <p className="text-xs text-text-secondary mt-1 line-clamp-2 min-h-[2rem]">
                    {renderData?.description || 'No description set for this capture yet.'}
                  </p>
                </div>
              )}

              {renderData?.mediaType === 'video' ? (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden">
                  {renderData.backgroundMode === 'image' && renderData.backgroundImageUrl ? (
                    <img src={renderData.backgroundImageUrl} alt="Capture background" className="absolute inset-0 w-full h-full object-cover" />
                  ) : renderData.backgroundMode === 'solid' ? (
                    <div className="absolute inset-0" style={{ backgroundColor: renderData.backgroundColor }} />
                  ) : renderData.imageUrl ? (
                    <img src={renderData.imageUrl} alt={renderData.title} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 bg-[#0D0D0D]" />
                  )}

                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: `rgba(0, 0, 0, ${backgroundOverlayAlpha})` }}
                  />

                  <div className="absolute inset-0 rounded-lg overflow-hidden border border-white/10 bg-black/40">
                    {renderData.imageUrl ? (
                      <img
                        src={renderData.imageUrl}
                        alt={`${renderData.title} thumbnail`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                        <Film className="w-8 h-8 text-white/70" />
                        <p className="text-[11px] font-mono text-white/70 uppercase tracking-wide">Video capture</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#0D0D0D]">
                  {renderData?.imageUrl || promotedItem.coverUrl ? (
                    <img
                      src={renderData?.imageUrl || promotedItem.coverUrl || ''}
                      alt={promotedItem.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[#0D0D0D] flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-text-muted" />
                    </div>
                  )}
                </div>
              )}

              <motion.div
                initial={false}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 flex items-center justify-center bg-black/45 rounded-lg"
                style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
              >
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-black/60 backdrop-blur-md border border-white/20 text-white text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  <span>Edit Promoted Capture</span>
                </div>
              </motion.div>

            </div>
          ) : (
            <div
              className="h-[320px] flex flex-col items-center justify-center cursor-pointer hover:bg-white/[0.02] transition-colors rounded-lg"
              onClick={() => setShowPicker(true)}
            >
              <Sparkles className="w-10 h-10 text-text-muted mb-3" />
              <p className="text-sm text-text-secondary font-medium">No promoted capture</p>
              <p className="text-xs text-text-muted mt-1">Click to select an active capture</p>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showPicker && (
          <PromotedContentPicker
            currentItem={promotedItem}
            captures={activeCaptures}
            onSelect={handleSelectItem}
            onClear={handleClear}
            onClose={() => setShowPicker(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PickerProps {
  currentItem: PromotedItem | null;
  captures: CaptureItem[];
  onSelect: (item: PromotedItem) => void;
  onClear: () => void;
  onClose: () => void;
}

function PromotedContentPicker({ currentItem, captures, onSelect, onClear, onClose }: PickerProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-lg bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">Select Promoted Capture</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="p-4 max-h-[400px] overflow-y-auto">
          {captures.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="w-8 h-8 text-text-muted mx-auto mb-3" />
              <p className="text-sm text-text-secondary">No active captures available</p>
              <p className="text-xs text-text-muted mt-1">Create a playable track capture or configured video capture first</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {captures.map((capture) => {
                const isSelected = currentItem?.id === capture.id;
                const isVideo = capture.mediaType === 'video';
                return (
                  <button
                    key={capture.id}
                    onClick={() => onSelect({
                      type: 'capture',
                      id: capture.id,
                      title: capture.title,
                      coverUrl: capture.imageUrl,
                      captureSlug: capture.slug,
                    })}
                    className={`relative rounded-xl overflow-hidden border transition-all ${
                      isSelected
                        ? 'border-electric-cyan ring-2 ring-electric-cyan/30'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="aspect-square bg-[#0D0D0D]">
                      {capture.imageUrl ? (
                        <img src={capture.imageUrl} alt={capture.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {isVideo ? <Film className="w-8 h-8 text-text-muted" /> : <ImageIcon className="w-8 h-8 text-text-muted" />}
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-[#111]">
                      <p className="text-xs text-white font-medium truncate">{capture.title}</p>
                      <p className="text-[10px] font-mono text-white/60 mt-0.5 uppercase">{isVideo ? 'Video' : 'Track'}</p>
                    </div>
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-electric-cyan flex items-center justify-center">
                        <span className="text-black text-xs font-bold">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
          {currentItem ? (
            <button
              onClick={onClear}
              className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
            >
              Remove Promoted Capture
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-muted hover:text-white bg-white/5 border border-white/10 rounded-lg hover:border-white/20 transition-colors"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
